import { ReactComponent as ImportDataSvg } from "@/components/ui/icons/import-data.svg";
import { useTrackElementSizes } from "@/hooks/use-track-element-sizes";
import { usePhoneViewerLayout } from "@/hooks/use-phone-viewer-layout";
import { selectModeName } from "@/store/mode-selectors";
import { selectFlyWalkSceneFilter } from "@/store/modes/flywalk-mode-slice";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { useIsReportToolActive } from "@/store/ui/ui-utils";
import { SceneFilter } from "@/types/scene-filter";
import { FaroButton, FaroSvgIcon, FaroText, grey } from "@faro-lotv/flat-ui";
import { useIntercom } from "@faro-lotv/foreign-observers";
import { Box, Stack } from "@mui/material";
import { useCallback, useEffect, useState, type JSX } from "react";
import { useUiOverlayContext } from "../common/ui-overlay-provider";
import { useActiveCamera } from "../r3f/cameras/active-camera-context";
import { AnalysisToolBarTool } from "./analysis-toolbar/analysis-tool";
import { CameraSwitch } from "./camera-switch";
import { CanvasToolbar } from "./canvas-toolbar";
import { ModeSwitch } from "./mode-switch";
import { MobileViewerControls } from "./mobile-viewer-controls";
import { useTreeContext } from "./tree/tree-context";
import { ViewSettingsToolbar } from "./view-settings-toolbar/view-settings-toolbar";

/**
 * @returns Overlay to render for all modes
 */
export function AllModesOverlay(): JSX.Element {
  const activeTool = useAppSelector(selectActiveTool);
  const isPhone = usePhoneViewerLayout();

  const currMode = useAppSelector(selectModeName);
  const {
    cameraProjection,
    setCameraProjection,
    isProjectionSwitchVisible,
    predefinedView,
    setPredefinedView,
  } = useActiveCamera();

  // Currently Gaussian Splats do not support orthographic projection
  const isViewingGaussianSplats =
    useAppSelector(selectFlyWalkSceneFilter) === SceneFilter.GaussianSplats;

  const shouldShowSmartNavigationToggle = currMode === "overview";

  const shouldShowProjectionOption =
    currMode === "overview" &&
    isProjectionSwitchVisible &&
    !isViewingGaussianSplats;

  const shouldShowAnalysisToolBar =
    activeTool !== ToolName.clipScene &&
    (currMode === "overview" || currMode === "walk");

  // Communicate the available space in the bottom right to the other mode-specific overlays
  const { setBottomRightAvailableHeight } = useUiOverlayContext();
  const [bottomRightContainer, setBottomRightContainer] = useState<
    HTMLDivElement | undefined
  >();
  const { height } = useTrackElementSizes(bottomRightContainer);
  useEffect(() => {
    setBottomRightAvailableHeight(height);
  }, [height, setBottomRightAvailableHeight]);

  // TODO: remove this once the report and snapshot tools will be their own modes
  const isReportToolActive = useIsReportToolActive();
  const { tree } = useTreeContext();
  const showEmptyPage = isReportToolActive && tree.length === 0;

  if (isPhone) {
    return (
      <>
        <MobileViewerControls
          cameraControls={
            <CameraSwitch
              shouldShowProjectionOption={shouldShowProjectionOption}
              shouldShowSmartNavigationToggle={shouldShowSmartNavigationToggle}
              cameraOptions3D={cameraProjection}
              onCameraChange={setCameraProjection}
              showPredefinedViewsOptions
              predefinedView={predefinedView}
              onPredefinedViewSelected={setPredefinedView}
            />
          }
        />
        <CanvasToolbar />
        {shouldShowAnalysisToolBar && activeTool === ToolName.analysis && (
          <Box
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              maxWidth: "calc(100% - 24px)",
              pointerEvents: "auto",
            }}
          >
            <AnalysisToolBarTool />
          </Box>
        )}
      </>
    );
  }

  return (
    <Box
      component="div"
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      {showEmptyPage && <EmptyReportToolOverlay />}
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          width: "100%",
          height: "100%",
          p: 2,
        }}
      >
        <Stack direction="column" spacing="1em" sx={{ justifyContent: "end" }}>
          <CameraSwitch
            shouldShowProjectionOption={shouldShowProjectionOption}
            shouldShowSmartNavigationToggle={shouldShowSmartNavigationToggle}
            cameraOptions3D={cameraProjection}
            onCameraChange={setCameraProjection}
            showPredefinedViewsOptions
            predefinedView={predefinedView}
            onPredefinedViewSelected={setPredefinedView}
          />

          <ModeSwitch />
        </Stack>

        <Stack
          direction="column"
          sx={{
            height: "100%",
            alignSelf: "center",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <Stack direction="column" sx={{ flexBasis: 0, flexGrow: 1 }}>
            <ViewSettingsToolbar />
          </Stack>
          <Stack direction="row" sx={{ gap: 2 }}>
            {shouldShowAnalysisToolBar && (
              <Stack direction="column" sx={{ justifyContent: "center" }}>
                <AnalysisToolBarTool />
              </Stack>
            )}
            <Stack
              direction="column"
              sx={{ flexBasis: 0, flexGrow: 1, justifyContent: "center" }}
            >
              <CanvasToolbar />
            </Stack>
          </Stack>
          {/** box used to ensure the CanvasToolbar is centered in the column */}
          <Box
            ref={setBottomRightContainer}
            component="div"
            sx={{ flexBasis: 0, flexGrow: 1 }}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

function EmptyReportToolOverlay(): JSX.Element {
  const intercom = useIntercom();
  const openIntercom = useCallback(() => intercom?.showMessenger(), [intercom]);
  const openHelpPage = useCallback(
    () =>
      window.open(
        "https://help.holobuilder.com/en/collections/15503529-waypoints-in-sphere-xg",
        "_blank",
      ),
    [],
  );

  return (
    <Stack
      direction="column"
      sx={{
        width: "100%",
        height: "100%",
        textAlign: "center",
        position: "absolute",
        backgroundColor: grey[0],
        justifyContent: "center",
        alignItems: "center",
        gap: 2.5,
      }}
    >
      {/* Illustration */}
      <FaroSvgIcon
        source={ImportDataSvg}
        aria-label="import data icon"
        sx={{
          width: "300px",
          height: "200px",
        }}
      />
      <FaroText variant="heading32">
        No compatible items in the project
      </FaroText>
      <FaroText variant="heading20" sx={{ color: grey[600] }}>
        This features only supports 360° and 2D photos.
      </FaroText>
      <FaroText variant="heading20" sx={{ color: grey[600] }}>
        To get started, add compatible data to your project.
      </FaroText>
      <Stack direction="column" sx={{ py: "30px", gap: "20px" }}>
        {intercom && (
          <FaroButton variant="secondary" size="l" onClick={openIntercom}>
            Get help from our Support team
          </FaroButton>
        )}

        <FaroButton variant="ghost" size="l" onClick={openHelpPage}>
          Learn more about 360 workflows
        </FaroButton>
      </Stack>
    </Stack>
  );
}
